import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QuestaoService from "../../sdk/services/QuestaoService";
import { TagDto } from "../../sdk/@types/TagDto";
import useQuestionFiltersStore from "../store/useQuestionFiltersStore";

/**
 * Option sourcing for the question filter.
 *
 * This used to be split in two: DefaultLayout fetched agencies and subjects and
 * passed joined "YYYY / Nome" strings down as props, while Filter.tsx kept its
 * own module-level caches of the same two calls plus the tag fetch. Both live
 * here now, and agencies stay structured so Ano and Instituição can be offered
 * as independent dimensions.
 */

export interface AgencyOption {
  id: string;
  year: string;
  name: string;
}

export interface SubjectOption {
  id: string;
  name: string;
}

/** Module-level cache with in-flight de-duplication. */
let agenciesPromise: Promise<AgencyOption[]> | null = null;
let subjectsPromise: Promise<SubjectOption[]> | null = null;

function fetchAgencies(): Promise<AgencyOption[]> {
  if (!agenciesPromise) {
    // Subscriber-scoped like fetchSubjects below: the unscoped /testingagencies
    // lists the whole catalog, including bancas with no questions in this
    // user's plan (the admin mocktest form still wants that one).
    agenciesPromise = QuestaoService.getTestingAgenciesBySubscriber()
      .then((rows: any[]) =>
        (rows || [])
          .map((a: any) => ({
            id: String(a?.id ?? ""),
            year: String(a?.year ?? "").trim(),
            name: String(a?.name ?? "").trim(),
          }))
          .filter((a: AgencyOption) => a.year !== "" || a.name !== ""),
      )
      .catch((error) => {
        agenciesPromise = null;
        throw error;
      });
  }
  return agenciesPromise;
}

function fetchSubjects(): Promise<SubjectOption[]> {
  if (!subjectsPromise) {
    subjectsPromise = QuestaoService.getSubjectsBySubscriber()
      .then((rows: any[]) =>
        (rows || [])
          .map((s: any) => ({ id: String(s?.id ?? ""), name: String(s?.name ?? "").trim() }))
          .filter((s: SubjectOption) => s.name !== ""),
      )
      .catch((error) => {
        subjectsPromise = null;
        throw error;
      });
  }
  return subjectsPromise;
}

/** Exposed for tests and for forcing a refresh after an admin edit. */
export function resetFilterOptionsCache() {
  agenciesPromise = null;
  subjectsPromise = null;
}

function normalizeTags(tags: TagDto.TagDto[] | null | undefined): string[] {
  return Array.from(
    new Set((tags || []).map((t) => (t.description || "").trim()).filter(Boolean)),
  );
}

export interface FilterOptions {
  years: string[];
  agencyNames: string[];
  subjectNames: string[];
  keywords: string[];
  loadingBase: boolean;
  loadingKeywords: boolean;
  baseError: boolean;
  keywordsError: boolean;
  reload: () => void;
}

export default function useFilterOptions(): FilterOptions {
  const draft = useQuestionFiltersStore((s) => s.draft);
  const pruneKeywords = useQuestionFiltersStore((s) => s.pruneKeywords);

  const [agencies, setAgencies] = useState<AgencyOption[]>([]);
  const [subjects, setSubjects] = useState<SubjectOption[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);

  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [baseError, setBaseError] = useState(false);
  const [keywordsError, setKeywordsError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const reload = useCallback(() => {
    resetFilterOptionsCache();
    setReloadToken((t) => t + 1);
  }, []);

  // ---------------------------------------------------------------- base data
  useEffect(() => {
    let cancelled = false;
    setLoadingBase(true);
    setBaseError(false);

    Promise.all([fetchAgencies(), fetchSubjects()])
      .then(([agencyRows, subjectRows]) => {
        if (cancelled) return;
        setAgencies(agencyRows);
        setSubjects(subjectRows);
      })
      .catch(() => {
        if (cancelled) return;
        setBaseError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingBase(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const years = useMemo(
    () =>
      Array.from(new Set(agencies.map((a) => a.year).filter(Boolean))).sort((a, b) =>
        b.localeCompare(a, "pt-BR", { numeric: true }),
      ),
    [agencies],
  );

  const agencyNames = useMemo(
    () =>
      Array.from(new Set(agencies.map((a) => a.name).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, "pt-BR"),
      ),
    [agencies],
  );

  const subjectNames = useMemo(
    () =>
      Array.from(new Set(subjects.map((s) => s.name))).sort((a, b) =>
        a.localeCompare(b, "pt-BR"),
      ),
    [subjects],
  );

  // The tag endpoint still speaks in agency IDs. With Ano and Instituição split,
  // a selection no longer maps to one agency: picking only 2025 means every
  // agency of 2025, and picking only CBMGO means CBMGO in every year. Resolve
  // the intersection of whichever dimensions are actually set.
  const selectedYears = draft.years;
  const selectedAgencies = draft.agencies;
  const selectedSubjects = draft.subjects;

  const agencyIdsKey = useMemo(() => {
    if (selectedYears.length === 0 && selectedAgencies.length === 0) return "";
    const yearSet = new Set(selectedYears);
    const nameSet = new Set(selectedAgencies);
    return agencies
      .filter(
        (a) =>
          (yearSet.size === 0 || yearSet.has(a.year)) &&
          (nameSet.size === 0 || nameSet.has(a.name)),
      )
      .map((a) => a.id)
      .filter(Boolean)
      .sort()
      .join(",");
  }, [agencies, selectedYears, selectedAgencies]);

  const subjectIdsKey = useMemo(() => {
    if (selectedSubjects.length === 0) return "";
    const nameSet = new Set(selectedSubjects);
    return subjects
      .filter((s) => nameSet.has(s.name))
      .map((s) => s.id)
      .filter(Boolean)
      .sort()
      .join(",");
  }, [subjects, selectedSubjects]);

  const hasContext =
    selectedYears.length > 0 || selectedAgencies.length > 0 || selectedSubjects.length > 0;

  // ------------------------------------------------------------------- tags
  useEffect(() => {
    if (loadingBase) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current;
      setLoadingKeywords(true);
      setKeywordsError(false);

      const request = hasContext
        ? QuestaoService.getTagsByContextSubscriber({
            testingAgencyIds: agencyIdsKey ? agencyIdsKey.split(",") : [],
            subjectIds: subjectIdsKey ? subjectIdsKey.split(",") : [],
          })
        : QuestaoService.buscarTagsDasPalavrasChaves();

      request
        .then((tags: TagDto.TagDto[]) => {
          if (requestId !== requestIdRef.current) return;
          const normalized = normalizeTags(tags);
          setKeywords(normalized);
          // A keyword that no longer exists in this context cannot be applied,
          // so drop it from the draft rather than leaving a dead token.
          pruneKeywords(normalized);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setKeywords([]);
          setKeywordsError(true);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoadingKeywords(false);
        });
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [loadingBase, hasContext, agencyIdsKey, subjectIdsKey, reloadToken, pruneKeywords]);

  return {
    years,
    agencyNames,
    subjectNames,
    keywords,
    loadingBase,
    loadingKeywords,
    baseError,
    keywordsError,
    reload,
  };
}
